import { ExportLinkType } from '@aibees/elephant/hooks/types';
import { Enrichment } from '@aibees/shared/definition';
import { useDownloadMutation } from '@aibees/shared/hooks';
import { classnames } from '@aibees/shared/utils';
import { LoadingButton } from '@mui/lab';
import { isEmpty } from 'lodash-es';
import { cloneElement, memo, useCallback, useState } from 'react';
import { useGetExportLinkApi } from '../../api/use-get-export-link-api';
import * as Styled from './styles';
import type { ExportFileActionProps as Props } from './types';

export const useExportFileDownload = (item: Props['item'] | Enrichment) => {
  const [inProgress, setInProgress] = useState(false);

  const api = useGetExportLinkApi(
    {
      id: item.id,
      type: ExportLinkType.GENERAL,
      status: item.status,
    },
    {
      enabled: false,
    }
  );

  const { mutate: downloadFileMutation, isLoading: isDownloadingFile } =
    useDownloadMutation(item.name.replace('.csv', ''), 'csv'); // remove .csv from filename for the enrichment table case

  const downloadFile = useCallback(async () => {
    try {
      setInProgress(true);
      const result = await api.refetch();
      if (result.data?.url) {
        downloadFileMutation({ url: result.data.url });
      }
    } catch (error) {
      console.error('Download failed:', error);
    } finally {
      setInProgress(false);
    }
  }, [api, downloadFileMutation]);

  return {
    downloadFile,
    isDownloading: inProgress || isDownloadingFile || api.isFetching,
    isError: api.isError,
  };
};

export const ExportFileAction = memo(({ className, item, element }: Props) => {
  const { downloadFile, isDownloading } = useExportFileDownload(item);

  if (!!item.files && isEmpty(item.files)) return null;

  return (
    <Styled.Wrapper className={classnames('ExportFileAction', className)}>
      {element ? (
        cloneElement(element, {
          onClick: downloadFile,
          disabled: isDownloading,
        })
      ) : (
        <LoadingButton
          variant="contained"
          size="xsmall"
          color="primary"
          onClick={downloadFile}
          loading={isDownloading}
        >
          Download
        </LoadingButton>
      )}
    </Styled.Wrapper>
  );
});

ExportFileAction.displayName = 'ExportFileAction';
